import { StartSearch } from '../Results/types';
import { Meal } from '../Meal/types';
import { Baggage, Measurement } from '../BaggageService/types';
import { StepsEnum } from '../types';
import {
	BaggageType,
	CheckoutOrderFragment,
	FlightStaticInfoFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment,
	SelectedSeatFragment,
	TravellerGdsServiceService,
	TimelineStep,
	Trip_AeroexpressTrip_Fragment,
	InsuranceProgramFragment,
	NASProductCategory,
	NASProductFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
} from '@websky/graphql';
import { PassengerDisabilitiesState } from '../PassengerForm/components/components/Disabilities/types';
import { SelectedService } from '../AIPurchasePredictionService/types';
import { ServiceMeta } from './store/selectedServices/actions';
export { StepsEnum as StepType } from '../types';
import { SelectedSeatsServiceType } from './store/selectedServices/reducers';
import { Money } from '../types';
import { PassengerFilesState } from '../PassengerForm/hooks';

export interface CheckoutProps {
	orderId: string;
	startNewSearch: StartSearch;
	order?: CheckoutOrderFragment;
}

export interface PassengerValues {
	[name: string]: string | PassengerDisabilitiesState | PassengerFilesState;
}

export interface FormValues {
	customer?: {
		phone?: string;
		email?: string;
		accompanyingPersonName?: string;
		accompanyingPersonTicketNumber?: string;
		accompanyingCheck?: boolean;
		subscribed?: boolean;
	};
	passengers: PassengerValues[];
	subscribed?: boolean;
	terms?: boolean;
}

export interface RunServiceRequestPassengers {
	passengerId: string;
	setServices?: TravellerGdsServiceService[];
	seats?: SelectedSeatFragment[];
}

export type RunServiceRequestCallback = (order: CheckoutOrderFragment) => void;
export type RunServiceRequest = (
	passengers: RunServiceRequestPassengers[],
	onSuccess?: RunServiceRequestCallback
) => void;

export interface AllowedDocuments {
	Passport: boolean;
	MilitaryIDCard: boolean;
	DocOfPassportLusing: boolean;
	DiplomaticPassport: boolean;
	InternationalPassportNotRU: boolean;
	InternationalPassportRU: boolean;
	InternationalPassport: boolean;
	NationalPassport: boolean;
	SeamanDocument: boolean;
	SeniorDocument: boolean;
	BirthRegDocument: boolean;
	ResidencePermit: boolean;
}

export interface AllowedDocumentsByCountry {
	other: Partial<AllowedDocuments>;
	ruChildren: Partial<AllowedDocuments>;

	[countryCode: string]: Partial<AllowedDocuments>;
}

export interface Step {
	url: string;
	type: StepsEnum;
	conditions?: {
		bookedOnly?: boolean;
	};
	hidden?: boolean;
	disabled?: boolean;
}

export interface City {
	name: string;
}

export interface Airport {
	name: string;
	iata: string;
	city?: City;
}

export interface Location {
	airport: Airport;
}

export interface Segment {
	id?: string;
	arrival: Location;
	departure: Location;
	groupId?: string;
	amenities?: FlightStaticInfoFragment['amenities'];
}

export interface PassengerLabels {
	[passengerId: string]: string;
}

export interface TravellerParameter<T> {
	type?: T;
	value?: string;
}

export interface Passenger extends PassengerFragment {
	passengerId?: string;
	supplierTravellerType?: string;
}

export interface AvailableGdsServices {
	[name: string]: OrderAdditionalServiceGdsServiceServiceType | BaggageType;
}

export interface GdsAggregationSelectedService {
	service: GdsAggregationService;
	type: GdsAggregationServiceType;
	count: number;
}

export type GdsAggregationServiceBaggage = {
	_serviceType: 'gdsService';
} & Baggage;

export type GdsAggregationServiceMeal = {
	_serviceType: 'gdsService';
} & Meal;

export type GdsAggregationServiceSeat = {
	_serviceType: 'seat';
} & SelectedSeatsServiceType;

export type GdsAggregationServiceInsurance = {
	_serviceType: 'insurance';
} & InsuranceProgramFragment;

export type GdsAggregationServiceMisc = {
	_serviceType: 'gdsService';
} & GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

export type GdsAggregationServiceInsuranceGroups = {
	_serviceType: 'insuranceGroups';
	price?: Money;
	groups: {
		groupName: string;
		insurancePrograms: InsuranceProgramFragment[];
	}[];
};

export type GdsAggregationServiceAeroexpress = {
	_serviceType: 'aeroexpress';
} & Trip_AeroexpressTrip_Fragment;

export type GdsAggregationServiceNas = {
	_serviceType: 'nas';
} & NASProductFragment;

export type GdsAggregationServiceVip = {
	_serviceType: 'gdsService';
} & GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

export type EveryTravelService = {
	_serviceType: 'everyTravel';
	excursionId: number;
	productId: number;
	title: string;
	price: Money;
	imageUrl: string;
	date: string;
	time: string;
};

export type GdsAggregationService =
	| GdsAggregationServiceBaggage
	| GdsAggregationServiceMeal
	| GdsAggregationServiceSeat
	| GdsAggregationServiceInsurance
	| GdsAggregationServiceInsuranceGroups
	| GdsAggregationServiceAeroexpress
	| GdsAggregationServiceMisc
	| GdsAggregationServiceNas
	| EveryTravelService
	| GdsAggregationServiceVip;

export type GdsAggregationServiceType =
	| OrderAdditionalServiceGdsServiceServiceType
	| NASProductCategory
	| 'baggageDelivery'
	| 'animal'
	| 'transfer'
	| 'insurance'
	| 'insuranceGroups'
	| 'loyalty'
	| 'CarryOn'
	| 'everyTravel'
	| 'upgrade';

export interface GdsAggregationServiceGroup {
	type: GdsAggregationServiceType;
	services: GdsAggregationService[];
	selectedServices: GdsAggregationSelectedService[];
	canAddService: boolean;
	minPrice?: Money;
	totalPrice?: Money;
	step?: TimelineStep;
}

export interface GdsServicesAggregations {
	segmentId?: string;
	international?: boolean;
	services: GdsAggregationServiceGroup[];
}

export interface ServicesByAggregationType {
	type: TimelineStep;
	segmentId: string;
	minutes: {
		from: number;
		to: number;
	};
	services: GdsAggregationServiceGroup[];
	transferSegment?: boolean;
}

export const convertMealObject = (service: GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment): Meal => ({
	id: service.id,
	name: service.name,
	description: service.description,
	price: service.price,
	imageURL: service.image,
	icon: service.icon,
	category: service.category,
	weight: service.mealWeight,
	allergens: service.allergens ? [service.allergens] : null,
	segmentsId: service.allowedSegments.reduce((segmentIds, group) => [...segmentIds, ...group], []),
	allowedPassengers: service.allowedPassengers.map(group => group[0]),
	canBeAdded: service.canBeAdded,
	allowedSegments: service.allowedSegments,
	rfics: service.rfisc,
	type: service.type
});

export const convertBaggageObject = (
	service: GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment
): Baggage => ({
	id: service.id,
	allowedPassengers: service.allowedPassengers.map(group => group[0]),
	segmentIds: service.allowedSegments,
	type: service.baggageType,
	value: _getBaggageWeightValue(service.baggageWeight),
	size: service.size,
	included: 0,
	price: {
		amount: service.price.amount,
		currency: service.price.currency
	},
	name: service.name,
	description: service.description,
	canBeAdded: service.canBeAdded,
	rfics: service.rfisc,
	isReplacedBy: service.isReplacedBy
});

const _getBaggageWeightValue = (baggage?: string | null): Baggage['value'] => {
	const baggageMatch = baggage?.match(/\d/);

	if (!baggageMatch) {
		return { amount: null, measurement: Measurement.Kilograms };
	}

	return {
		prefix: baggage.substring(0, baggageMatch.index).trim(),
		amount: parseInt(baggage.match(/\d/g).join('')),
		measurement: Measurement.Kilograms
	};
};

export type ServiceOpenEvent = {
	service: SelectedService;
	callback: () => Promise<void>;
	meta: ServiceMeta;
};

export type BaggageServiceOpenEvent = {
	type: BaggageType;
	meta?: ServiceMeta;
};

export interface SegmentDirection {
	iata: string;
	cityName: string;
}

export interface SegmentValue {
	id: string;
	label: string;
	departure: SegmentDirection;
	arrival: SegmentDirection;
	date: Date;
}

export interface TravellerValue {
	id: string;
	type: string;
	supplierTravellerType?: string;
	linkedTraveller?: TravellerValue;
	values: {
		firstName: string;
		lastName: string;
		middleName: string;
		fullName: string;
		email: string;
		phone: string;
	};
}

export enum PaymentResultStatus {
	Success = 'success',
	Fail = 'fail'
}
